import { defaultConfig, type RulesConfig, type ShotZone } from "../core/config.js";
import { generateTeamSetup } from "../core/teamSetup.js";

/**
 * The rules of a match, as a plain settings object.
 *
 * This is the exact set of knobs the local game\'s settings panel offers, made
 * portable: the same shape a browser edits is what a tournament host saves and
 * what the server turns into a RulesConfig when it creates a fixture\'s room.
 * One converter serves both, so "the same settings as local games" stays true
 * by construction rather than by keeping two mappings in step.
 *
 * Device preferences (sound, piece style, board design) are deliberately not
 * here: a host dictates the rules of play, not how another player\'s board is
 * allowed to look.
 */
export interface MatchRuleSettings {
  fieldWidth: number;
  fieldHeight: number;
  goalWidth: number;
  numDefenders: number;
  numMidfielders: number;
  numStrikers: number;
  anyPieceCanPlayBall: boolean;
  offside: boolean;
  ballRetrieval: boolean;
  ballFlyOverOtherPlayers: boolean;
  ballFlyOverShooter: boolean;
  playerCanStepOnBall: boolean;
  movementCapEnabled: boolean;
  movementCapMax: number;
  passDistanceEnabled: boolean;
  passDistanceMax: number;
  /**
   * One number instead of a zone table: the keeper's reach grows by one every
   * `saveZoneLength` squares from the goal. The default is 4 (0 within 4,
   * 1 within 8, 2 within 12, ...); length 3 was the earlier classic ladder.
   * The old editable per-boundary list collapsed into this because every
   * ruleset anyone actually built was uniform anyway.
   */
  saveZoneLength: number;
  turnLimitEnabled: boolean;
  turnLimit: number;
  clockEnabled: boolean;
  clockBaseMinutes: number;
  clockIncrementSeconds: number;
}

export const DEFAULT_MATCH_RULE_SETTINGS: MatchRuleSettings = {
  fieldWidth: 13,
  fieldHeight: 20,
  goalWidth: 7,
  numDefenders: 2,
  numMidfielders: 1,
  numStrikers: 2,
  anyPieceCanPlayBall: true,
  offside: true,
  ballRetrieval: false,
  ballFlyOverOtherPlayers: true,
  ballFlyOverShooter: false,
  playerCanStepOnBall: false,
  movementCapEnabled: true,
  movementCapMax: 5,
  passDistanceEnabled: true,
  passDistanceMax: 10,
  saveZoneLength: 4,
  turnLimitEnabled: true,
  turnLimit: 90,
  clockEnabled: true,
  clockBaseMinutes: defaultConfig.clock!.baseTimeMs / 60_000,
  clockIncrementSeconds: defaultConfig.clock!.incrementMs / 1000,
};

const clamp = (raw: unknown, fallback: number, min: number, max: number): number => {
  const value = typeof raw === "number" && Number.isFinite(raw) ? raw : fallback;
  return Math.max(min, Math.min(max, value));
};
const flag = (raw: unknown, fallback: boolean): boolean =>
  (typeof raw === "boolean" ? raw : fallback);

/**
 * Turn untrusted input into settings a server can act on, or null.
 *
 * Everything is clamped to the same ranges the local page enforces, because
 * this shape now arrives over HTTP from whoever holds a host session: a
 * 10,000-wide board is a denial of service, not a preference. Unknown keys are
 * dropped rather than carried.
 */
export function sanitizeMatchRuleSettings(input: unknown): MatchRuleSettings | null {
  if (typeof input !== "object" || input === null || Array.isArray(input)) return null;
  const raw = input as Record<string, unknown>;
  const d = DEFAULT_MATCH_RULE_SETTINGS;

  return {
    fieldWidth: Math.round(clamp(raw["fieldWidth"], d.fieldWidth, 7, 25)),
    fieldHeight: Math.round(clamp(raw["fieldHeight"], d.fieldHeight, 10, 30)),
    goalWidth: Math.round(clamp(raw["goalWidth"], d.goalWidth, 1, 25)),
    numDefenders: Math.round(clamp(raw["numDefenders"], d.numDefenders, 0, 5)),
    numMidfielders: Math.round(clamp(raw["numMidfielders"], d.numMidfielders, 0, 5)),
    numStrikers: Math.round(clamp(raw["numStrikers"], d.numStrikers, 0, 5)),
    anyPieceCanPlayBall: flag(raw["anyPieceCanPlayBall"], d.anyPieceCanPlayBall),
    offside: flag(raw["offside"], d.offside),
    ballRetrieval: flag(raw["ballRetrieval"], d.ballRetrieval),
    ballFlyOverOtherPlayers: flag(raw["ballFlyOverOtherPlayers"], d.ballFlyOverOtherPlayers),
    ballFlyOverShooter: flag(raw["ballFlyOverShooter"], d.ballFlyOverShooter),
    playerCanStepOnBall: flag(raw["playerCanStepOnBall"], d.playerCanStepOnBall),
    movementCapEnabled: flag(raw["movementCapEnabled"], d.movementCapEnabled),
    movementCapMax: Math.round(clamp(raw["movementCapMax"], d.movementCapMax, 1, 12)),
    passDistanceEnabled: flag(raw["passDistanceEnabled"], d.passDistanceEnabled),
    passDistanceMax: Math.round(clamp(raw["passDistanceMax"], d.passDistanceMax, 1, 40)),
    saveZoneLength: Math.round(clamp(raw["saveZoneLength"], d.saveZoneLength, 1, 12)),
    turnLimitEnabled: flag(raw["turnLimitEnabled"], d.turnLimitEnabled),
    turnLimit: Math.round(clamp(raw["turnLimit"], d.turnLimit, 1, 500)),
    clockEnabled: flag(raw["clockEnabled"], d.clockEnabled),
    clockBaseMinutes: clamp(raw["clockBaseMinutes"], d.clockBaseMinutes, 0.5, 180),
    clockIncrementSeconds: clamp(raw["clockIncrementSeconds"], d.clockIncrementSeconds, 0, 120),
  };
}

/**
 * Uniform save-zone bands from one length.
 *
 * Reach 0 within the first band, growing by one per band until the bands have
 * crossed the pitch; the last zone is open-ended so no shot falls outside the
 * table. Length 3 on a 20-deep board yields the classic ladder (3/0, 6/1,
 * 9/2, 12/3, ...) -- the keeper is marginally stronger than the historic
 * table against shots from beyond 15 squares, which the pass and shot ranges
 * make practically unreachable anyway.
 */
export function saveZonesFromLength(length: number, fieldHeight: number): ShotZone[] {
  const bandLength = Math.max(1, Math.round(length));
  const zones: ShotZone[] = [];
  let reach = 0;
  for (let edge = bandLength; edge < fieldHeight; edge += bandLength) {
    zones.push({ maxDistanceFromGoal: edge, goalkeeperReach: reach });
    reach += 1;
  }
  zones.push({ maxDistanceFromGoal: Infinity, goalkeeperReach: reach });
  return zones;
}

/**
 * The settings as a playable RulesConfig.
 *
 * Line-for-line the mapping LocalGamePage\'s custom games have always used --
 * it moved here rather than being duplicated, so a host-dictated tournament
 * fixture and a local custom game with the same settings are the same game.
 */
export function matchRuleSettingsToConfig(settings: MatchRuleSettings): RulesConfig {
  const w = Math.max(7, Math.min(25, Math.round(settings.fieldWidth)));
  const h = Math.max(10, Math.min(30, Math.round(settings.fieldHeight)));

  let boxW = Math.round(w * 9 / 13);
  boxW = Math.max(3, Math.min(w - 2, boxW));
  const boxH = Math.max(2, Math.round(h * 4 / 20));
  let goalW = Math.max(1, Math.min(w, Math.round(settings.goalWidth)));
  goalW = Math.min(boxW, goalW);

  const numDef = Math.max(0, Math.min(5, settings.numDefenders));
  const numMid = Math.max(0, Math.min(5, settings.numMidfielders));
  const numStr = Math.max(0, Math.min(5, settings.numStrikers));

  const setup = generateTeamSetup(w, h, boxH, numDef, numMid, numStr);

  const zones = saveZonesFromLength(settings.saveZoneLength, h);
  return {
    ...defaultConfig,
    board: { width: w, height: h },
    boxes: { width: boxW, height: boxH },
    goals: { width: goalW },
    ball: {
      flyOverOtherPlayers: settings.ballFlyOverOtherPlayers,
      flyOverShooter: settings.ballFlyOverShooter,
      playerCanStepOnBall: settings.playerCanStepOnBall,
    },
    globalPlayerMoveLimit: settings.movementCapEnabled
      ? settings.movementCapMax
      : "infinite" as number | "infinite",
    pass: {
      ...defaultConfig.pass,
      maxDistance: settings.passDistanceEnabled
        ? settings.passDistanceMax
        : "infinite" as number | "infinite",
    },
    shooting: { zones },
    anyPieceCanPlayBall: settings.anyPieceCanPlayBall,
    offside: settings.offside,
    ballRetrieval: settings.ballRetrieval,
    turnMode: "move-then-shoot",
    scoring: settings.turnLimitEnabled
      // Two halves with a kickoff switch at the break; the referee lets an
      // undefended final-third attack finish before either whistle.
      ? { mode: "turnLimit" as const, turns: Math.max(1, settings.turnLimit), halfTime: true }
      : { mode: "infinite" as const },
    clock: {
      enabled: settings.clockEnabled,
      baseTimeMs: Math.max(0.5, settings.clockBaseMinutes) * 60_000,
      incrementMs: Math.max(0, settings.clockIncrementSeconds) * 1000,
    },
    teamSetup: setup,
  };
}
